import Debug from 'debug';
import { safeGetError } from '../../lib/metrics';
import { LOG_TYPE, MetricService } from '../services/MetricService';
import { OfficeError } from '../errors';

import type { ComposeBase } from './types';

type CustomProps = 'TRACK_EMAIL' | 'SKIP_TRACKING_ERROR';

/** Gets the current item as an OutlookComposeItem. */
export function getComposeItem(): ComposeBase {
  if (!Office.context.mailbox.item) {
    throw new Error('Could not get compose item');
  }
  return Office.context.mailbox.item;
}

/**
 * Gets the current item as an OutlookReadItem.
 * @throws
 */
export function getReadItem() {
  if (!Office.context.mailbox.item) {
    throw new Error('Could not get read item');
  }
  return Office.context.mailbox.item;
}

/**
 * Determine if this email has a boolean custom property set (or null if it does not).
 * @arg item The item to query custom properties.
 * @arg key The property name to search for.
 * @returns a promise that will resolve to a boolean (if the property is set) or null if it
 * does not exist.
 */
export async function getBooleanCustomProperty(item: ComposeBase, key: CustomProps): Promise<boolean | null> {
  const debug = Debug('o365:custom_prop');

  return new Promise((resolve) => {
    debug('getBooleanCustomProperty: start');
    item.loadCustomPropertiesAsync((result) => {
      debug('getBooleanCustomProperty: properties', result);
      if (result.status === Office.AsyncResultStatus.Failed) {
        MetricService.log('error getting custom property', LOG_TYPE.ERROR, {
          ...safeGetError(new OfficeError(result.error)),
          key,
        }).then(() => resolve(null));

        return;
      }

      const properties = result.value;
      const value = properties.get(key);
      debug(`getBooleanCustomProperty: ${key}`, value);
      if (value === 'yes' || value === 'no') {
        return resolve(value === 'yes');
      }

      return resolve(null);
    });
  });
}

/**
 * Sets (or removes) a custom property on the provided email.
 * @arg item The item to receive the custom property.
 * @arg key The property name to set (or remove)
 * @arg value The value to set the property (if boolean) or remove (if null)
 * @returns a promise that will indicate if the property changed and the save succeeded or failed.
 */
export async function setBooleanCustomProperty(
  item: ComposeBase,
  key: CustomProps,
  value: boolean | null,
): Promise<boolean | null> {
  const debug = Debug('o365:custom_prop');

  return new Promise((resolve) => {
    debug('setBooleanCustomProperty: start');
    item.loadCustomPropertiesAsync((result) => {
      debug('setBooleanCustomProperty: result', result);
      if (result.status === Office.AsyncResultStatus.Failed) {
        MetricService.log('error loading custom property for setting', LOG_TYPE.ERROR, {
          ...safeGetError(new OfficeError(result.error)),
          key,
          value,
        }).then(() => resolve(null));

        return;
      }

      const properties = result.value;
      if (typeof value === 'boolean') {
        properties.set(key, value ? 'yes' : 'no');
        debug(`setBooleanCustomProperty: ${key}`, value);
      } else {
        properties.remove(key);
        debug(`setBooleanCustomProperty: removed ${key}`);
      }

      properties.saveAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          MetricService.log('error saving custom property', LOG_TYPE.ERROR, {
            ...safeGetError(new OfficeError(result.error)),
            key,
            value,
          }).then(() => resolve(null));

          return;
        }

        debug('setBooleanCustomProperty: saved');
        resolve(true);
      });
    });
  });
}
